import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Space,
  Table,
  Typography
} from "antd"
import type { FormInstance } from "antd/es/form/hooks/useForm"
import React from "react"

import { recalcPaymentScheduleAmounts } from "../../../../../utils/documents/recalc-payment-schedule-amounts.ts"
import { formListAddFocus } from "../../../../../utils/form/form-list-add-focus.ts"
import { budgetFormat } from "../../../../../utils/number/budget-format.ts"
import {
  validateNumber,
  validatePositiveNumber
} from "../../../../../utils/validation/number.ts"
import type {
  TemplateDocumentCostSummary,
  TemplateDocumentFormData,
  TemplateDocumentScheduleItem
} from "../../../types/template-document-form-data.tsx"

function recalcTotals(form: FormInstance<TemplateDocumentFormData>) {
  const key: keyof TemplateDocumentFormData = "cost_summary"
  const values = form.getFieldValue(key) as TemplateDocumentCostSummary
  const lineItems = values?.line_items || []
  const discountAmount = values?.discount_amount || 0

  const subtotal = lineItems.reduce((sum: number, item) => {
    return sum + (+item?.amount || 0)
  }, 0)

  const total = subtotal - discountAmount

  const schedule: TemplateDocumentScheduleItem[] =
    form.getFieldValue("payment_schedule") || []
  const syncedSchedule = recalcPaymentScheduleAmounts(schedule, total)

  form.setFieldsValue({
    [key]: {
      ...values,
      subtotal,
      total
    },
    ...(syncedSchedule ? { payment_schedule: syncedSchedule } : {})
  })
}

interface CostSummaryBlockProps {
  form: FormInstance<TemplateDocumentFormData>
  index: number
}

export function CostSummaryBlock({ form, index }: CostSummaryBlockProps) {
  const key: keyof TemplateDocumentFormData = "cost_summary"

  const subtotal = Form.useWatch([key, "subtotal"], form) || 0
  const total = Form.useWatch([key, "total"], form) || 0

  const hiddenColumns =
    Form.useWatch(["blocks", index, "config", "hidden_columns"], form) || []

  return (
    <>
      {/* register in the form */}
      <Form.Item<TemplateDocumentFormData> name={[key, "subtotal"]} hidden>
        <input />
      </Form.Item>
      <Form.Item<TemplateDocumentFormData> name={[key, "total"]} hidden>
        <input />
      </Form.Item>

      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {/* Line Items Table */}
        <Form.List name={[key, "line_items"]}>
          {(fields, { add, remove }) => (
            <Table
              pagination={false}
              dataSource={fields}
              scroll={{ x: "max-content" }}
              rowKey="key"
              bordered
              columns={[
                {
                  hidden: hiddenColumns?.includes("item"),
                  title: "Item",
                  // width: "20%",
                  dataIndex: "name",
                  key: "item",
                  render: (_, field) => (
                    <Form.Item
                      name={[field.name, "item"]}
                      style={{ margin: 0 }}
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: "Required"
                        }
                      ]}
                    >
                      <Input placeholder="Enter name" />
                    </Form.Item>
                  )
                },
                {
                  hidden: hiddenColumns?.includes("description"),
                  title: "Description",
                  dataIndex: "description",
                  key: "description",
                  render: (_, field) => (
                    <Form.Item
                      name={[field.name, "description"]}
                      style={{ margin: 0 }}
                      rules={[
                        {
                          required: false,
                          whitespace: true,
                          message: "Required"
                        }
                      ]}
                    >
                      <Input placeholder="Enter description" />
                    </Form.Item>
                  )
                },
                {
                  hidden: hiddenColumns?.includes("amount"),
                  title: "Amount",
                  width: 250,
                  dataIndex: "name",
                  key: "amount",
                  align: "right",
                  render: (_, field) => (
                    <Form.Item
                      name={[field.name, "amount"]}
                      style={{ margin: 0 }}
                      rules={[
                        { required: true, message: "Required" },
                        { validator: validatePositiveNumber }
                      ]}
                    >
                      <InputNumber
                        stringMode
                        style={{ width: "100%" }}
                        placeholder="0"
                        suffix={"$"}
                        min={0}
                        max={99999999.99}
                        precision={2}
                        onChange={() => recalcTotals(form)}
                      />
                    </Form.Item>
                  )
                },
                {
                  title: "",
                  width: 50,
                  render: (_, field) => (
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        remove(field.name)
                        // setTimeout щоб form values оновились після remove
                        setTimeout(() => recalcTotals(form), 0)
                      }}
                    />
                  )
                }
              ]}
              footer={() => (
                <Button
                  type="dashed"
                  onClick={() => {
                    formListAddFocus<TemplateDocumentFormData>(
                      add,
                      fields,
                      form,
                      [key, "line_items"],
                      ["item"]
                    )
                  }}
                  block
                  icon={<PlusOutlined />}
                >
                  Cost Item
                </Button>
              )}
            />
          )}
        </Form.List>

        {/* Cost Summary Section */}
        <div
          style={{
            padding: "16px",
            backgroundColor: "#fafafa",
            borderRadius: "8px"
          }}
        >
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            {/* Subtotal */}
            <Row justify="space-between" align="middle">
              <Col>
                <Typography.Text strong>Subtotal</Typography.Text>
              </Col>
              <Col>
                <Typography.Text strong>
                  {budgetFormat(subtotal)}
                </Typography.Text>
              </Col>
            </Row>

            {/* Discount */}
            <Row gutter={16} align="middle">
              <Col flex="auto">
                <Form.Item
                  name={[key, "discount_description"]}
                  style={{ margin: 0 }}
                >
                  <Input placeholder="Discount description (optional)" />
                </Form.Item>
              </Col>
              <Col style={{ minWidth: "120px" }}>
                <Form.Item
                  name={[key, "discount_amount"]}
                  style={{ margin: 0 }}
                  rules={[{ validator: validateNumber }]}
                >
                  <InputNumber
                    className={"[&_input]:text-red-500"}
                    style={{ width: "100%" }}
                    max={subtotal}
                    placeholder="0"
                    suffix={"$"}
                    min={0}
                    precision={2}
                    onChange={() => recalcTotals(form)}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Total */}
            <Row
              justify="space-between"
              align="middle"
              style={{ borderTop: "1px solid #d9d9d9", paddingTop: "12px" }}
            >
              <Col>
                <Typography.Text strong style={{ fontSize: "16px" }}>
                  Total
                </Typography.Text>
              </Col>
              <Col>
                <Typography.Text strong style={{ fontSize: "16px" }}>
                  {budgetFormat(total)}
                </Typography.Text>
              </Col>
            </Row>
          </Space>
        </div>
      </Space>
    </>
  )
}
